import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import { FilterFormWrapper } from '@/components/FilterFormWrapper'
import { Select } from '@/components/ui/select'
import { fetchUnits } from '@/api/units.api'
import { PROTOCOL_OPTIONS } from '@/components/form/MailForm'

export interface MailFilterValues {
  mail?: string
  name?: string
  port?: string
  protocol?: number
  unitId?: string
  unitName?: string
  time?: DateRangeValue
  status?: number
}

interface MailFilterPanelProps {
  values: MailFilterValues
  onChange: (values: MailFilterValues) => void
  onSearch: () => void
  showUnitColumn?: boolean
}

export function MailFilterPanel({
  values,
  onChange,
  onSearch,
  showUnitColumn = true,
}: MailFilterPanelProps) {
  function set<TKey extends keyof MailFilterValues>(
    key: TKey,
    value: MailFilterValues[TKey],
  ) {
    onChange({ ...values, [key]: value })
  }

  return (
    <FilterFormWrapper
      onSearch={onSearch}
      className="rounded-xl border border-gray-300 bg-white px-6 py-5"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex min-w-[150px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Địa chỉ mail</span>
            <Input
              placeholder="Nhập địa chỉ mail"
              value={values.mail || ''}
              onChange={(e: { target: { value: string } }) =>
                set('mail', e.target.value)
              }
            />
          </div>

          <div className="flex min-w-[150px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Tên hiển thị</span>
            <Input
              placeholder="Nhập tên hiển thị"
              value={values.name || ''}
              onChange={(e: { target: { value: string } }) =>
                set('name', e.target.value)
              }
            />
          </div>

          <div className="flex min-w-[150px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Cổng</span>
            <Input
              placeholder="Nhập cổng"
              value={values.port}
              onChange={(e: { target: { value: string } }) =>
                set('port', e.target.value)
              }
            />
          </div>

          <div className="flex min-w-[140px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Giao thức</span>
            <Select
              placeholder="Tất cả"
              value={
                values.protocol !== undefined && !Number.isNaN(values.protocol)
                  ? values.protocol.toString()
                  : undefined
              }
              className="w-full"
              onValueChange={(v) => set('protocol', v)}
              allowClear
              options={PROTOCOL_OPTIONS.map((opt) => ({
                value: opt.value.toString(),
                label: opt.label,
              }))}
            />
          </div>

          {showUnitColumn && (
            <div className="flex min-w-[140px] flex-1 flex-col gap-1">
              <span className="text-sm font-medium">Đơn vị</span>
              <Select
                initialPage={0}
                cacheKey="mail-filter-unit-select"
                lazy
                placeholder="Tất cả"
                value={values.unitId || undefined}
                className="w-full"
                allowClear
                enableRemoteSearch
                allLabel="Tất cả"
                initialOptions={
                  values.unitId && values.unitName
                    ? [{ value: values.unitId, label: values.unitName }]
                    : []
                }
                loadOptions={async ({ page, signal, searchText }) => {
                  const res = await fetchUnits(
                    page,
                    10,
                    { keySearch: searchText?.trim() || undefined },
                    signal,
                  )
                  const arr = Array.isArray(res.items) ? res.items : []
                  const hasNextPage = arr.length >= 10
                  return {
                    options: arr.map((u: any) => ({
                      value: u.id,
                      label: u.name,
                    })),
                    hasNextPage,
                    nextPage: hasNextPage ? page + 1 : undefined,
                  }
                }}
                onLazyValueChange={(unitId, option) => {
                  onChange({
                    ...values,
                    unitId: unitId,
                    unitName: option?.label ? String(option.label) : undefined,
                  })
                }}
              />
            </div>
          )}

          <div className="flex min-w-[140px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Trạng thái</span>
            <Select
              placeholder="Tất cả"
              value={values.status}
              className="w-full"
              onValueChange={(v) => set('status', v)}
              allowClear
              options={[
                { value: 1, label: 'Hoạt động' },
                { value: 0, label: 'Không hoạt động' },
              ]}
            />
          </div>

          <div className="flex min-w-[240px] flex-1 flex-col gap-1">
            <span className="text-sm font-medium">Thời gian</span>
            <DateRangePicker
              value={values.time}
              onChange={(v) => set('time', v)}
              className="w-full rounded-full"
            />
          </div>

          <Button
            variant="outline"
            size="md"
            className="gap-2 rounded-full border-[#CE7A58] text-[#CE7A58] hover:bg-[#CE7A58]/10 h-10 px-6"
            onClick={onSearch}
          >
            <Search size={16} /> Tìm kiếm
          </Button>
        </div>
      </div>
    </FilterFormWrapper>
  )
}
